/**
 * The one definition of "which language is the UI in", and the one place
 * that turns it into a BCP-47 locale.
 *
 * Before this module every app declared its own `Lang = 'de' | 'en'` and
 * every formatter re-derived its own locale inline, which is how four
 * different English locales ended up in the tree: `en-AT`, `en-IE`,
 * `en-GB` and `en-US`. They are not interchangeable —
 *
 *   en-US  →  08/20/2026, 04:30 PM   (month first, 12-hour)
 *   en-AT  →  20/08/2026, 16:30      but € 1.234,50 (German digits)
 *   en-IE  →  20/08/2026, 16:30      and €1,234.50
 *
 * `en-IE` is the one that reads correctly for both dates and money, so it
 * is what `localeFor` returns. Anything that needs a different locale is a
 * bug, not a preference.
 */

/** The UI language. Apps re-export this rather than declaring their own. */
export type Lang = 'de' | 'en'

const LOCALES: Record<Lang, string> = {
  de: 'de-AT',
  en: 'en-IE',
}

/** The BCP-47 locale for a UI language. */
export function localeFor(lang: Lang): string {
  return LOCALES[lang] ?? LOCALES.de
}

/** Narrow an unknown value (storage, URL, `navigator.language`) to a `Lang`. */
export function asLang(value: unknown): Lang | null {
  return value === 'de' || value === 'en' ? value : null
}

/**
 * The workshop's wall clock.
 *
 * Every rendered timestamp and every business date is Vienna time — the
 * shop is in Vienna and its opening hours, invoice due dates and slot grid
 * are all expressed in that zone. Formatting in the *browser's* zone is
 * only ever correct by accident, so the shared formatters pin this by
 * default rather than asking each call site to remember.
 */
export const SHOP_TIME_ZONE = 'Europe/Vienna'
